// src/components/OrganizationSelector.js - Supabase対応版
import React, { useState, useEffect } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';

const OrganizationSelector = () => {
  const {
    currentOrganization,
    userOrganizations,
    isMultiTenant,
    switchOrganization,
    userRole,
    currentHospital,
  } = useOrganization();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const addHoverEffects = () => {
      const existingStyle = document.getElementById('org-selector-styles');
      if (!existingStyle) {
        const style = document.createElement('style');
        style.id = 'org-selector-styles';
        style.textContent = `
          .org-selector:hover {
            background-color: #f8f9fa !important;
          }
          .org-dropdown-item:hover {
            background-color: #f8f9fa !important;
          }
        `;
        document.head.appendChild(style);
      }
    };

    addHoverEffects();
    
    return () => {
      const style = document.getElementById('org-selector-styles');
      if (style) {
        document.head.removeChild(style);
      }
    };
  }, []);

  if (!currentOrganization) {
    return null;
  }

  // 病院名 (hospitals.name) を優先。
  //   - hospital row が取れていて name がある → 病院名を表示
  //   - row は取れているが name 空 → "(病院名未設定)" (= hospitals.id はあるが name 空)
  //   - row 自体取れない (null) → 組織タイプのラベル (「病院」等) にフォールバック
  const hospitalLabel = currentHospital
    ? (currentHospital.name || '(病院名未設定)')
    : getOrgTypeName(currentOrganization.type);

  // 組織が1つしかない場合は簡潔に表示
  if (!isMultiTenant) {
    return (
      <div style={styles.singleOrgHeader}>
        <div style={styles.orgInfo}>
          <div style={styles.orgName}>{currentOrganization.name}</div>
          <div style={styles.orgType}>
            {hospitalLabel}
          </div>
        </div>
        <div style={styles.userRole}>
          {getRoleDisplayName(userRole)}
        </div>
      </div>
    );
  }

  // 複数組織の場合はドロップダウン表示
  return (
    <div style={styles.container}>
      <div
        className="org-selector"
        style={styles.selector}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div style={styles.currentOrg}>
          <div style={styles.orgIcon}>
            {getOrgIcon(currentOrganization.type)}
          </div>
          <div style={styles.orgDetails}>
            <div style={styles.orgName}>{currentOrganization.name}</div>
            <div style={styles.roleAndType}>
              {getRoleDisplayName(userRole)} • {hospitalLabel}
            </div>
          </div>
        </div>
        <div style={styles.dropdownArrow}>
          {isDropdownOpen ? '▲' : '▼'}
        </div>
      </div>

      {isDropdownOpen && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>
            組織を選択
          </div>
          {userOrganizations.map((org) => (
            <div
              key={org.id}
              className="org-dropdown-item"
              style={{
                ...styles.dropdownItem,
                ...(org.id === currentOrganization.id ? styles.activeItem : {})
              }}
              onClick={() => {
                switchOrganization(org.id);
                setIsDropdownOpen(false);
              }}
            >
              <div style={styles.orgIcon}>
                {getOrgIcon(org.type)}
              </div>
              <div style={styles.orgDetails}>
                <div style={styles.dropdownOrgName}>{org.name}</div>
                <div style={styles.dropdownOrgMeta}>
                  {getRoleDisplayName(org.userRole)} • {getOrgTypeName(org.type)}
                </div>
              </div>
              {org.id === currentOrganization.id && (
                <div style={styles.checkmark}>✓</div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {isDropdownOpen && (
        <div 
          style={styles.overlay}
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

// ユーティリティ関数
const getRoleDisplayName = (role) => {
  const roleNames = {
    owner: 'オーナー',
    admin: '管理者',
    manager: 'マネージャー',
    staff: 'スタッフ'
  };
  return roleNames[role] || 'スタッフ';
};

const getOrgIcon = (type) => {
  const icons = {
    hospital: '🏥',
    clinic: '🏨',
    group: '🏢'
  };
  return icons[type] || '🏥';
};

const getOrgTypeName = (type) => {
  const typeNames = {
    hospital: '病院',
    clinic: 'クリニック',
    group: 'グループ'
  };
  return typeNames[type] || '病院';
};

// スタイル定義
const styles = {
  container: {
    position: 'relative',
    zIndex: 1000,
  },
  
  singleOrgHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
  },
  
  selector: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e9ecef',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  
  currentOrg: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  
  orgIcon: {
    fontSize: '20px',
    minWidth: '24px',
  },
  
  orgInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  
  orgDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  
  orgName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#212529',
    marginBottom: '2px',
  },
  
  orgType: {
    fontSize: '12px',
    color: '#6c757d',
  },
  
  roleAndType: {
    fontSize: '12px',
    color: '#6c757d',
  },
  
  userRole: {
    fontSize: '12px',
    color: '#6c757d',
    backgroundColor: '#e9ecef',
    padding: '4px 8px',
    borderRadius: '12px',
  },
  
  dropdownArrow: {
    fontSize: '12px',
    color: '#6c757d',
    transition: 'transform 0.2s ease',
  },
  
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  
  dropdownHeader: {
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
  },
  
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    borderBottom: '1px solid #f8f9fa',
  },
  
  activeItem: {
    backgroundColor: '#e3f2fd',
  },
  
  dropdownOrgName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#212529',
    marginBottom: '2px',
  },
  
  dropdownOrgMeta: {
    fontSize: '12px',
    color: '#6c757d',
  },
  
  checkmark: {
    marginLeft: 'auto',
    color: '#2196f3',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: -1,
  },
};

export default OrganizationSelector;